/* RESPONSIBLE TEAM: team-reporting */

import {
  DURATION_TICK_INTERVALS,
  DURATION_TICK_COUNT,
  VALUE_TICK_INTERVALS,
} from '@/features/reporting-charts/constants';

type DataPoint = number | null | [number, number | null] | { y: number | null; color: string };

interface SeriesDataSlice {
  metricIndex: number;
  data: DataPoint[];
}

/**
 * Returns the maximum Y data value across all series belonging to a given metric index.
 * Mirrors Ember's ColumnLineChartBuilder.getMinAndMaxValues(index) — starts at 0 and
 * rounds each value, so the result is always a non-negative integer.
 *
 * Handles all three Highcharts data point shapes:
 * - Plain number (e.g. bar/category charts)
 * - [x, y] tuple (time-series charts)
 * - { y, color } object (per-point colored charts)
 */
export function getMaxDataValueForMetric(series: SeriesDataSlice[], metricIndex: number): number {
  let max = 0;
  for (const s of series) {
    if (s.metricIndex !== metricIndex) continue;
    for (const point of s.data) {
      let y: number | null;
      if (Array.isArray(point)) {
        y = point[1];
      } else if (typeof point === 'object' && point !== null) {
        y = point.y;
      } else {
        y = point;
      }
      if (y !== null && Math.round(y) > max) {
        max = Math.round(y);
      }
    }
  }
  return max;
}

/**
 * Calculates an appropriate tick interval for duration data based on the data range.
 * Returns an interval that produces between 2-5 ticks with "nice" values.
 *
 * @param min - Minimum data value (in seconds)
 * @param max - Maximum data value (in seconds)
 * @returns A tick interval in seconds, or undefined if no suitable interval found
 */
export function getDurationTickInterval(min: number, max: number): number | undefined {
  const range = max - min;
  if (range === 0) return undefined;

  let bestInterval: number | undefined;
  for (const interval of DURATION_TICK_INTERVALS) {
    const tickCount = Math.floor(range / interval);
    if (tickCount >= DURATION_TICK_COUNT.MIN && tickCount <= DURATION_TICK_COUNT.MAX) {
      bestInterval = interval;
    }
  }
  return bestInterval;
}

export function getValueTickInterval(min: number, max: number): number | undefined {
  const range = max - min;
  if (range === 0) return undefined;

  let bestInterval: number | undefined;
  for (const interval of VALUE_TICK_INTERVALS) {
    const tickCount = Math.floor(range / interval);
    if (tickCount >= DURATION_TICK_COUNT.MIN && tickCount <= DURATION_TICK_COUNT.MAX) {
      bestInterval = interval;
    }
  }
  return bestInterval;
}
